/**
 * MOBILE HERO — SECTION 7 · "Schau rein, hör rein." follow cards + footer bar
 * (design map §2-7 · prototype lines 2117–2155).
 *
 * Centered serif H2, then the YouTube + Podcast link cards stacked in a COLUMN
 * (desktop puts them in a row), then the footer bar (dark triangle logo +
 * copyright left, Karriere / Datenschutz / Impressum right). Mobile paddings (0 18px).
 */
import { BrandLogoMark } from "@v1/ui/brand-logo-mark";
import { ArrowUpRight } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { CookieSettingsLink } from "@/lib/marketing-analytics/cookie-settings-link";
import { BRAND_LOGO_TITLE, MARKETING_LOGO_ALT } from "@/lib/marketing-seo";
import { useTranslations } from "next-intl";
import { SpotifyMark, YouTubeMark } from "./hero-social-brand-icons";
import { H } from "./hero-tokens";

const CARD_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 15,
  padding: "18px 20px",
  background: "#fff",
  border: "1px solid #ECEEF1",
  borderRadius: 16,
  textDecoration: "none",
  boxShadow:
    "0 1px 0 rgba(255,255,255,0.7) inset, 0 16px 34px -26px rgba(11,15,25,0.22)",
  transition:
    "border-color .18s ease, transform .18s ease, box-shadow .18s ease",
};

/** Neutral tile so the two brand marks are the only color on the card. */
const ICON_TILE: CSSProperties = {
  width: 48,
  height: 48,
  borderRadius: 14,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  background: "#F4F4F5",
  border: "1px solid rgba(11,15,25,0.06)",
};

const CARD_TITLE: CSSProperties = {
  display: "block",
  fontSize: 15.5,
  fontWeight: 600,
  color: H.ink,
};

const CARD_SUB: CSSProperties = {
  display: "block",
  fontSize: 13,
  color: "rgba(11,15,25,0.55)",
  marginTop: 1,
};

const ARROW: CSSProperties = {
  marginLeft: "auto",
  color: "rgba(11,15,25,0.32)",
  display: "inline-flex",
  flexShrink: 0,
};

export function HeroMobileFooter(): ReactNode {
  const t = useTranslations("marketing.heroMobileFooter");
  return (
    <footer style={{ margin: "56px auto 0", padding: "0 18px" }}>
      <div style={{ textAlign: "center" }}>
        <h2
          style={{
            fontFamily: H.display,
            fontWeight: 400,
            fontSize: 34,
            lineHeight: 1.1,
            letterSpacing: "-0.018em",
            color: H.ink,
            margin: "14px 0 0",
          }}
        >
          {t("schauReinHoerRein")}
        </h2>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          margin: "24px auto 0",
        }}
      >
        <a
          className="mkhero-social"
          href="https://www.youtube.com/@hausverwalter-ai"
          target="_blank"
          rel="noopener noreferrer"
          style={CARD_STYLE}
        >
          <span style={ICON_TILE}>
            <YouTubeMark />
          </span>
          <span style={{ minWidth: 0 }}>
            <span style={CARD_TITLE}>{t("youtube")}</span>
            <span style={CARD_SUB}>{t("softwareLiveErleben")}</span>
          </span>
          <span style={ARROW}>
            <ArrowUpRight width={18} height={18} strokeWidth={2} />
          </span>
        </a>

        <a
          className="mkhero-social"
          href="https://open.spotify.com/show/033k4UZU6bCuhiABtyxxFx"
          target="_blank"
          rel="noopener noreferrer"
          style={CARD_STYLE}
        >
          <span style={ICON_TILE}>
            <SpotifyMark />
          </span>
          <span style={{ minWidth: 0 }}>
            <span style={CARD_TITLE}>{t("podcast")}</span>
            <span style={CARD_SUB}>{t("prozesseHausverwaltung")}</span>
          </span>
          <span style={ARROW}>
            <ArrowUpRight width={18} height={18} strokeWidth={2} />
          </span>
        </a>
      </div>

      <div
        style={{
          marginTop: 46,
          paddingTop: 22,
          borderTop: "1px solid #ECEEF1",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            role="img"
            aria-label={MARKETING_LOGO_ALT}
            title={BRAND_LOGO_TITLE}
            style={{ display: "inline-flex", flexShrink: 0 }}
          >
            <BrandLogoMark width={24} height={24} variant="dark" />
          </span>
          <span style={{ fontSize: 13, color: "rgba(11,15,25,0.5)" }}>
            {t("k2026HausverwalterAi")}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 22,
            flexWrap: "wrap",
          }}
        >
          <a
            className="mkhero-footlink"
            aria-label={t("karriereseiteOeffnen")}
            href="/careers"
            style={{
              fontSize: 12.5,
              color: "rgba(11,15,25,0.5)",
              textDecoration: "none",
            }}
          >
            {t("karriere")}
          </a>
          <a
            className="mkhero-footlink"
            aria-label={t("datenschutzerklaerungOeffnen")}
            href="/datenschutz"
            style={{
              fontSize: 12.5,
              color: "rgba(11,15,25,0.5)",
              textDecoration: "none",
            }}
          >
            {t("datenschutz")}
          </a>
          <a
            className="mkhero-footlink"
            aria-label={t("impressumOeffnen")}
            href="/impressum"
            style={{
              fontSize: 12.5,
              color: "rgba(11,15,25,0.5)",
              textDecoration: "none",
            }}
          >
            {t("impressum")}
          </a>
          <CookieSettingsLink variant="marketing" />
        </div>
      </div>
    </footer>
  );
}
